import React, { useCallback, useMemo } from 'react';
import {
    Alert,
    Box,
    CircularProgress,
    IconButton,
    Stack,
    Tooltip,
    Typography,
} from '@mui/material';
import { CloudDownload } from '@mui/icons-material';
import { useBoolean } from 'react-hanger';
import to from 'await-to-js';
import { Button, Chip, DialogV2, FlashMessage } from 'c-components';
import { apiClient } from 'c-data';
import { NetworkRequestState } from 'c-data-layer';
import { useAPIClientRequest, useDateUtils } from 'c-hooks';
import { useCommonTranslation } from 'c-translation';
import { track } from 'c-lib';
import { ReportDateTimeframe } from 'c-reports/Types';
import useCampaignWrapperState from 'c-main/Components/Campaign/CampaignReports/useCampaignWrapperState';
import { cadExportFilters } from './filteredCadExportFilters';

type Props = {
    id: number;
};

const langPath = 'Modules.Main.Campaigns.Overview.Cad.FilteredExport';

/**
 * Queues a raw CAD export narrowed to the analytics filters and date range currently applied,
 * e.g. a single media owner's rows. Reads the same URL-backed state every analytics tab filters by.
 */
const FilteredCadExportButton: React.FC<Props> = ({ id }) => {
    const t = useCommonTranslation();
    const { formatDateStringIgnoreTime } = useDateUtils();
    const { environmentMode, hasPCAData, advancedFilters, urlDateValue } =
        useCampaignWrapperState(id);
    const dialogOpen = useBoolean(false);
    const { start, requestState, error, reset } = useAPIClientRequest(
        apiClient.Entities.Campaign.pcaExport,
    );

    const filters = useMemo(() => cadExportFilters(advancedFilters), [advancedFilters]);

    const dates = useMemo(() => {
        if (urlDateValue?.timeframe == null || urlDateValue.timeframe === ReportDateTimeframe.All) {
            return {};
        }

        return { start_date: urlDateValue.startDate, end_date: urlDateValue.endDate };
    }, [urlDateValue]);

    const openDialog = useCallback(() => {
        reset();
        dialogOpen.setTrue();
    }, [dialogOpen, reset]);

    const onSubmit = useCallback(async () => {
        track('export_cad', { campaign_id: id, environment: environmentMode });
        await to(start(id, { environment: environmentMode, filters, ...dates }));
    }, [dates, environmentMode, filters, id, start]);

    if (!hasPCAData) return null;

    const isLoading = requestState === NetworkRequestState.InProgress;
    const isSuccess = requestState === NetworkRequestState.Success;
    const filterEntries = Object.entries(filters);

    return (
        <>
            <Tooltip title={t(`${langPath}.tooltip`)}>
                <IconButton onClick={openDialog} color="primary" size="large">
                    <CloudDownload />
                </IconButton>
            </Tooltip>
            <DialogV2
                open={dialogOpen.value}
                onClose={dialogOpen.setFalse}
                maxWidth="sm"
                title={`${langPath}.title`}
                description={`${langPath}.description`}
                actions={
                    <Button
                        onClick={onSubmit}
                        disabled={isLoading || isSuccess}
                        startIcon={isLoading ? <CircularProgress size={16} /> : undefined}
                    >
                        {t(`${langPath}.submit`)}
                    </Button>
                }
            >
                <Stack gap={2}>
                    <Box>
                        <Typography variant="subtitle2">{t(`${langPath}.environment`)}</Typography>
                        <Typography>{environmentMode}</Typography>
                    </Box>
                    <Box>
                        <Typography variant="subtitle2">{t(`${langPath}.dateRange`)}</Typography>
                        <Typography>
                            {dates.start_date
                                ? `${formatDateStringIgnoreTime({
                                      date: dates.start_date,
                                  })} – ${formatDateStringIgnoreTime({ date: dates.end_date })}`
                                : t(`${langPath}.allDates`)}
                        </Typography>
                    </Box>
                    <Box>
                        <Typography variant="subtitle2">{t(`${langPath}.filters`)}</Typography>
                        {filterEntries.length === 0 ? (
                            <Typography>{t(`${langPath}.noFilters`)}</Typography>
                        ) : (
                            <Stack gap={1}>
                                {filterEntries.map(([key, values]) => (
                                    <Stack key={key} direction="row" gap={1} flexWrap="wrap">
                                        <Typography component="span" color="text.secondary">
                                            {t(
                                                `Modules.Main.Campaigns.Overview.Reports.fields.${key}`,
                                            )}
                                            :
                                        </Typography>
                                        {values.map(value => (
                                            <Chip
                                                key={value}
                                                label={value}
                                                size="small"
                                                variant="outlined"
                                            />
                                        ))}
                                    </Stack>
                                ))}
                            </Stack>
                        )}
                    </Box>
                    {filterEntries.length > 0 || dates.start_date ? (
                        <Alert severity="warning" variant="outlined">
                            {t(`${langPath}.partialWarning`)}
                        </Alert>
                    ) : null}
                    {isSuccess && (
                        <FlashMessage status="success">{t(`${langPath}.success`)}</FlashMessage>
                    )}
                    {error && <FlashMessage status="error">{String(error)}</FlashMessage>}
                </Stack>
            </DialogV2>
        </>
    );
};

export default FilteredCadExportButton;
